<!--   components is used to toggle the 'clever-search'  -->
<svelte:options customElement="clever-search-toggler" />

<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { fade, fly } from 'svelte/transition';
  import { TogglePositionTypes } from '../enums/toggle-position-types.enum';
  import { WidgetType } from '../enums/widget-type.enum';
  import { ConfigStore } from '../store/config';
  /** store */
  import {
    state_showSearch,
    state_showBadge,
    state_sessionStorage,
    state_searchResult,
  } from '../store/state';
  import { WindowsCustomEventTypes } from '../enums/windows-custom-event.enum';
  import { shadowDomUpdates } from '../helpers/shadowdom-updates';
  import { onMount, tick } from 'svelte';
  import ToggleGreeting from './toggle-greeting.svelte';
  import { SessionStoreHandler } from '../helpers/session-storage-handler';
  import { elasticInOut } from 'svelte/easing';
  import RatingCsat from './rating-csat.svelte';
  import RatingCsatNotification from './rating-csat-notification.svelte';
  import { state_conversationResults } from '../store/state';
  import { isIpad, isMobile } from '../helpers/detect-os';
  import LauncherTheme from './themes/launcher-theme/launcher-theme.svelte';
  import { CSATTypes } from '../enums/csat-types.enum';
  import { resolveRateableQuestionId } from '../helpers/rating-question-helper';

  $: getCssClass($ConfigStore.togglePosition);

  let wrapperClasses: string;
  let wrapperClassesCsat: string;
  $: csatQuestionId = resolveRateableQuestionId(
    $ConfigStore.widgetType,
    $state_conversationResults,
    $state_searchResult
  );

  let checkGreetingOnFirstToggle: boolean;
  let showGreetingPopup: boolean = true;
  let isGreetingInstant: boolean = false;

  let btnToggler: HTMLElement | null = null;

  /** Begin Rating  CSAT check */

  let isRated = false;
  let isNotification = false;
  let lengthOfQuestions = 0;

  /** End Rating  CSAT check */

  onMount(() => {
    shadowDomUpdates('clever-search-toggler', true);

    if (SessionStoreHandler.get()?.visible && !isMobile()) {
      //state_showSearch.set(true);
      window.dispatchEvent(
        new CustomEvent(WindowsCustomEventTypes.toggleView, { detail: true })
      );
    } else {
      // the visible flag also gates the session-inactivity greeting refresh
      // (search-controller), so clear it when the widget is not restored
      if (SessionStoreHandler.get()?.visible)
        SessionStoreHandler.update({ visible: false });
      greetingOnFirstToggleCheck();
    }
    /*if (state_showSearch)*/

    /** Begin check csat rating */
    csatRatingToggleCheck();
  });

  window.addEventListener(WindowsCustomEventTypes.toggleView, async (e) => {
    greetingOnFirstToggleCheck();

    if (!$state_showSearch) {
      await tick();
      console.log('state_showSearch', btnToggler);
      const focusable = btnToggler?.querySelector<HTMLElement>(
        'button, [role="button"]'
      );
      focusable?.focus();
    }
  });

  window.addEventListener(WindowsCustomEventTypes.showGreetingPopup, (e) => {
    isGreetingInstant = true;
    showGreetingPopup = true;
  });

  // Begin Rating csat
  window.addEventListener(WindowsCustomEventTypes.showCsatPopup, (e) => {
    csatRatingToggleCheck();
  });

  /** Begin two-stage open (accidental-tap protection) */

  let confirmOpenVisible = false;
  let confirmOpenShownAt = 0;
  let confirmOpenOffset = 8;
  let confirmOpenTimer: ReturnType<typeof setTimeout>;
  let btnConfirmOpen: HTMLElement | null = null;

  let confirmHoldActive = false;
  let confirmHoldTimer: ReturnType<typeof setTimeout>;

  $: confirmHoldTimeMs = Math.max(0, $ConfigStore.twoStageOpenHoldTime ?? 0);

  const isTwoStageOpen = (): boolean => {
    switch ($ConfigStore.twoStageOpen) {
      case 'all':
        return true;
      case 'touch':
        // primary pointer is coarse = touch-first device (not a touchscreen laptop)
        return window.matchMedia
          ? window.matchMedia('(pointer: coarse)').matches
          : 'ontouchstart' in window;
      default:
        return false;
    }
  };

  const showConfirmOpen = async (): Promise<void> => {
    clearTimeout(confirmOpenTimer);
    // random vertical offset so fixed-coordinate replay taps miss the pill
    confirmOpenOffset = 8 + Math.floor(Math.random() * 24);
    confirmOpenShownAt = performance.now();
    confirmOpenVisible = true;
    confirmOpenTimer = setTimeout(() => {
      hideConfirmOpen();
    }, $ConfigStore.twoStageOpenTimeout);
    await tick();
    btnConfirmOpen?.focus();
  };

  const hideConfirmOpen = (refocusLauncher: boolean = false): void => {
    confirmHoldCancel();
    clearTimeout(confirmOpenTimer);
    confirmOpenVisible = false;
    if (refocusLauncher)
      btnToggler
        ?.querySelector<HTMLElement>('button, [role="button"]')
        ?.focus();
  };

  /** rejects synthetic events and interactions arriving faster than a human
   * can react to the pill appearing (chained accidental/automated taps) */
  const isDeliberateConfirmEvent = (e: Event): boolean =>
    e.isTrusted && performance.now() - confirmOpenShownAt >= 300;

  const confirmOpenClick = (e: MouseEvent): void => {
    if (confirmHoldTimeMs > 0) return; // hold-to-open handles it
    if (!isDeliberateConfirmEvent(e)) return;

    hideConfirmOpen();
    dispatchToggleView();
  };

  const confirmHoldStart = (e: PointerEvent | KeyboardEvent): void => {
    if (confirmHoldTimeMs <= 0 || confirmHoldActive) return;
    if (!isDeliberateConfirmEvent(e)) return;
    if ('button' in e && e.button !== 0) return;

    if ('pointerId' in e) {
      // keep receiving pointerup even if the finger/cursor drifts off the pill
      try {
        btnConfirmOpen?.setPointerCapture(e.pointerId);
      } catch {}
    }

    confirmHoldActive = true;
    clearTimeout(confirmOpenTimer); // don't auto-hide mid-hold
    confirmHoldTimer = setTimeout(() => {
      confirmHoldActive = false;
      hideConfirmOpen();
      dispatchToggleView();
    }, confirmHoldTimeMs);
  };

  const confirmHoldCancel = (): void => {
    if (!confirmHoldActive) return;
    confirmHoldActive = false;
    clearTimeout(confirmHoldTimer);
    confirmOpenTimer = setTimeout(() => {
      hideConfirmOpen();
    }, $ConfigStore.twoStageOpenTimeout);
  };

  const confirmOpenKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      hideConfirmOpen(true);
      return;
    }
    if ((e.key === 'Enter' || e.key === ' ') && confirmHoldTimeMs > 0) {
      // suppress the native click so only a completed hold can open
      e.preventDefault();
      if (!e.repeat) confirmHoldStart(e);
    }
  };

  const confirmOpenKeyup = (e: KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') confirmHoldCancel();
  };

  /** End two-stage open */

  /** method to show/hide search panel*/
  const toggleView = () => {
    if (!$state_showSearch && isTwoStageOpen()) {
      showConfirmOpen();
      return;
    }

    dispatchToggleView();
    //state_showSearch.set(!$state_showSearch);
    /** only on chatbot type view, the greeting message is loading when the chat interface is toggle for the first time. */
    //greetingOnFirstToggleCheck();
  };

  const dispatchToggleView = (): void => {
    window.dispatchEvent(
      new CustomEvent(WindowsCustomEventTypes.toggleView, {
        detail: !$state_showSearch,
      })
    );
  };

  /** keyboard activation for the non-button launcher variants (Enter/Space only) */
  const toggleViewKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleView();
    }
  };

  const greetingOnFirstToggleCheck = (): void => {
    if (!$state_showSearch) return;

    showGreetingPopup = false;

    if (checkGreetingOnFirstToggle) return;

    checkGreetingOnFirstToggle = true;

    //if ($ConfigStore.noGreeting) return;

    window.dispatchEvent(
      new CustomEvent(WindowsCustomEventTypes.requestGreeting)
    );
  };

  //Rating  CSAT check
  const csatRatingToggleCheck = (): void => {
    if (!$state_sessionStorage) return;
    const csatRating = $state_sessionStorage?.csatRating;

    if (csatRating) {
      isRated = csatRating.isRated;
      isNotification = csatRating.isNotification;
      lengthOfQuestions = csatRating.lengthOfQuestions;
    }
  };

  const checkCSATOnce = (): boolean => {
    return (
      $ConfigStore.csatTypes === CSATTypes.once &&
      $state_conversationResults.length > 1 &&
      // Check threadId changes
      SessionStoreHandler.get()?.threadId !== $state_sessionStorage?.threadId &&
      !isRated
    );
  };

  const getCssClass = (position: TogglePositionTypes): void => {
    let css = 'toggler-wrapper';

    switch (position) {
      case TogglePositionTypes.leftBottom:
        css += ' cs-flt-lb';
        break;
      case TogglePositionTypes.leftTop:
        css += ' cs-flt-lt';
        break;
      case TogglePositionTypes.rightBottom:
        css += ' cs-flt-rb';
        break;
      case TogglePositionTypes.rightTop:
        css += ' cs-flt-rt';
        break;
      default:
        break;
    }

    setTimeout(() => {
      if (isIpad()) {
        wrapperClasses = css + ' ipad-only';
      } else {
        wrapperClasses = css;
      }
      if ($ConfigStore.showCsat) {
        wrapperClassesCsat = 'toggler-wrapper-csat';
      }
    }, $ConfigStore.toggleInitShowDelay);
  };
</script>

{#if !!$ConfigStore.apiBaseUrl && $state_showBadge}
  {#if ($ConfigStore.widgetType === WidgetType.cb || $ConfigStore.widgetType === WidgetType.ctt || $ConfigStore.widgetType === WidgetType.cmt) && !$state_showSearch && !!wrapperClasses}
    <div
      bind:this={btnToggler}
      class={wrapperClasses}
      in:fly|global={{
        y: 80,
        duration: 2000,
        easing: elasticInOut,
      }}
      out:fade|global
      style:bottom={$ConfigStore.customTheme.launchBottomPos}
      style:right={$ConfigStore.customTheme.launchRightPos}
      style:--cs-mobile-launch-right-postion={$ConfigStore.customTheme
        .launchRightMobilePos || $ConfigStore.customTheme.launchRightPos}
      style:--bottom-ipad={$ConfigStore.customTheme.launchBottomIpad
        ? $ConfigStore.customTheme.launchBottomIpad
        : $ConfigStore.customTheme.launchBottomPos}
    >
      {#if ($ConfigStore.toggleSrcHtml?.length ?? 0) > 0 && ($ConfigStore.toggleSrcImg?.length ?? 0) > 0}
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <button
          class="cs-tgl-btn"
          on:click={toggleView}
          aria-label={$ConfigStore.nameTogglerBtn || $_('header.show_chat')}
          title={$ConfigStore.nameTogglerBtn || $_('header.show_chat')}
          style:width={$ConfigStore.customTheme.buttonWidthHtml}
        >
          <p
            class="cs-tgl-html"
            style:bottom={$ConfigStore.customTheme.bottomTextHtmlPos}
            style:right={$ConfigStore.customTheme.rightTextHtmlPos}
            style:padding={$ConfigStore.customTheme.paddingHtml}
            style:background-color={$ConfigStore.customTheme.bgColorHtml}
            style:border-radius={$ConfigStore.customTheme.borderRadiusHtml}
            style:color={$ConfigStore.customTheme.textColorHtml}
            style:font-size={$ConfigStore.customTheme.textSizeHtml}
            style:border={$ConfigStore.customTheme.borderHtml}
          >
            {@html $ConfigStore.toggleSrcHtml}
          </p>
          <img
            class="cs-tgl-img"
            src={$ConfigStore.toggleSrcImg}
            alt={$ConfigStore.nameTogglerBtn || $_('header.show_chat')}
            style:max-height={$ConfigStore.customTheme.maxHeightImage}
            style:bottom={$ConfigStore.customTheme.bottomImagePos}
            style:right={$ConfigStore.customTheme.rightImagePos}
          />
        </button>
      {:else if ($ConfigStore.toggleSrcHtml?.length ?? 0) > 0}
        <div
          role="button"
          tabindex="0"
          aria-label={$ConfigStore.nameTogglerBtn || $_('header.show_chat')}
          on:click={toggleView}
          on:keydown={toggleViewKeydown}
        >
          {@html $ConfigStore.toggleSrcHtml}
        </div>
      {:else if ($ConfigStore.toggleSrcImg?.length ?? 0) > 0}
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <button
          class="cs-tgl-img-btn"
          on:click={toggleView}
          aria-label={$ConfigStore.nameTogglerBtn || $_('header.show_chat')}
        >
          <img
            class="cs-tgl-img"
            src={$ConfigStore.toggleSrcImg}
            alt={$ConfigStore.nameTogglerBtn || $_('header.show_chat')}
          />
        </button>
      {:else}
        <button
          on:click={toggleView}
          tabindex="0"
          aria-label={$ConfigStore.nameTogglerBtn || $_('header.show_chat')}
          class="cs-tgl-btn-logo"
        >
          <LauncherTheme />
        </button>
        <!-- <svg
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
          width="974"
          height="803"
          class="cs-tgl-default"
          viewBox="0 0 974 803"
          on:click={toggleView}
          on:keydown={toggleView}
          role="button"
          tabindex="0"
          aria-label={$ConfigStore.nameTogglerBtn || $_('header.show_chat')}
          aria-expanded={true}
          aria-controls={$ConfigStore.nameTogglerBtn || $_('header.show_chat')}
        >
          <defs>
            <filter
              id="filter"
              x="21"
              y="22.969"
              width="487"
              height="650.343"
              filterUnits="userSpaceOnUse"
            >
              <feFlood result="flood" flood-color="#555" />
              <feComposite
                result="composite"
                operator="in"
                in2="SourceGraphic"
              />
              <feBlend result="blend" in2="SourceGraphic" />
            </filter>
            <filter
              id="filter-2"
              x="156.875"
              y="29.344"
              width="795.406"
              height="745.656"
              filterUnits="userSpaceOnUse"
            >
              <feFlood result="flood" flood-color="#2696c1" />
              <feComposite
                result="composite"
                operator="in"
                in2="SourceGraphic"
              />
              <feBlend result="blend" in2="SourceGraphic" />
            </filter>
            <filter
              id="filter-3"
              x="332"
              y="318"
              width="101"
              height="101"
              filterUnits="userSpaceOnUse"
            >
              <feFlood result="flood" flood-color="#fff" />
              <feComposite
                result="composite"
                operator="in"
                in2="SourceGraphic"
              />
              <feBlend result="blend" in2="SourceGraphic" />
            </filter>
            <filter
              id="filter-4"
              x="496"
              y="318"
              width="101"
              height="101"
              filterUnits="userSpaceOnUse"
            >
              <feFlood result="flood" flood-color="#fff" />
              <feComposite
                result="composite"
                operator="in"
                in2="SourceGraphic"
              />
              <feBlend result="blend" in2="SourceGraphic" />
            </filter>
            <filter
              id="filter-5"
              x="659"
              y="318"
              width="101"
              height="101"
              filterUnits="userSpaceOnUse"
            >
              <feFlood result="flood" flood-color="#fff" />
              <feComposite
                result="composite"
                operator="in"
                in2="SourceGraphic"
              />
              <feBlend result="blend" in2="SourceGraphic" />
            </filter>
          </defs>
          <g id="Artboard_1" data-name="Artboard 1">
            <g style="fill: #e30000; filter: url(#filter)">
              <path
                id="path-2"
                style:fill={$ConfigStore.customTheme.badgeIconColor}
                class="cls-1"
                d="M508,638l-79,9L210,671S32.511,705.581,21,508V181S24.261-10.169,241,28L507,78l-2,7Z"
                style="stroke: inherit; filter: none; fill: inherit"
              />
            </g>
            <use
              xlink:href="#path-2"
              style="stroke: #000; filter: none; fill: none"
            />
            <g style="fill: #e30000; filter: url(#filter-2)">
              <path
                id="path"
                style:fill={$ConfigStore.customTheme.badgeBg}
                class="cls-2"
                d="M547,775l36-107s6.918-9.187-11-11-252-28-252-28-162.755-8.812-163-159,0-191,0-191-7.467-135,152-164S761,31,761,31,951.341,0,952,206s0,322,0,322S928.3,696,769,678l-24-2s-16.691-7-38,6S547,775,547,775Z"
                style="stroke: inherit; filter: none; fill: inherit"
              />
            </g>
            <use
              xlink:href="#path"
              style="stroke: #000; filter: none; fill: none"
            />
            <g style="fill: #e30000; filter: url(#filter-3)">
              <circle
                id="circle-3"
                class="cls-3"
                cx="382.5"
                cy="368.5"
                r="50.5"
                style="stroke: inherit; filter: none; fill: inherit"
              />
            </g>
            <use
              xlink:href="#circle-3"
              style="stroke: #000; filter: none; fill: none"
            />
            <g style="fill: #e30000; filter: url(#filter-4)">
              <circle
                id="circle-2"
                class="cls-4"
                cx="546.5"
                cy="368.5"
                r="50.5"
                style="stroke: inherit; filter: none; fill: inherit"
              />
            </g>
            <use
              xlink:href="#circle-2"
              style="stroke: #000; filter: none; fill: none"
            />
            <g style="fill: #e30000; filter: url(#filter-5)">
              <circle
                id="circle"
                class="cls-5"
                cx="709.5"
                cy="368.5"
                r="50.5"
                style="stroke: inherit; filter: none; fill: inherit"
              />
            </g>
            <use
              xlink:href="#circle"
              style="stroke: #000; filter: none; fill: none"
            />
          </g>
        </svg> -->
      {/if}

      {#if confirmOpenVisible}
        <div
          class="cs-tgl-confirm-wrapper"
          style:--cs-confirm-offset="{confirmOpenOffset}px"
          in:fly|global={{ y: 10, duration: 200 }}
          out:fade|global={{ duration: 150 }}
        >
          <button
            bind:this={btnConfirmOpen}
            class="cs-tgl-confirm-btn"
            class:cs-holding={confirmHoldActive}
            style:--cs-hold-time="{confirmHoldTimeMs}ms"
            on:click={confirmOpenClick}
            on:pointerdown={confirmHoldStart}
            on:pointerup={confirmHoldCancel}
            on:pointercancel={confirmHoldCancel}
            on:keydown={confirmOpenKeydown}
            on:keyup={confirmOpenKeyup}
            on:contextmenu|preventDefault
          >
            <span class="cs-tgl-confirm-progress" aria-hidden="true"></span>
            <span class="cs-tgl-confirm-label">
              {$ConfigStore.twoStageOpenLabel || $_('header.confirm_open')}
            </span>
          </button>
        </div>
      {/if}

      {#if showGreetingPopup}
        <ToggleGreeting instant={isGreetingInstant}></ToggleGreeting>
      {/if}
    </div>
  {/if}
  <!-- Begin Rating csat -->
  {#if !$state_showSearch && !!wrapperClasses && $ConfigStore.showCsat}
    <div
      class={wrapperClassesCsat}
      in:fly|global={{
        y: 80,
        duration: 2000,
        easing: elasticInOut,
      }}
      out:fade|global
      style:bottom={$ConfigStore.customTheme.launchBottomPos}
      style:right={$ConfigStore.customTheme.launchRightPos}
      style:--bottom-ipad={$ConfigStore.customTheme.launchBottomIpad
        ? $ConfigStore.customTheme.launchBottomIpad
        : $ConfigStore.customTheme.launchBottomPos}
    >
      <div
        class="cs-csat-rating-outer-wrapper"
        style:bottom={$ConfigStore.csatTheme.launchBottomPos}
        style:right={$ConfigStore.csatTheme.launchRightPos}
        style:--bottom-csat={$ConfigStore.csatTheme.launchBottomMobilePos}
      >
        {#if isNotification && $ConfigStore.showCsatResponse}
          <RatingCsatNotification />
        {:else if csatQuestionId && ((!isRated && $state_conversationResults.length > 1) || checkCSATOnce() || ($ConfigStore.csatTypes === CSATTypes.multitime && isRated && $state_conversationResults.length > lengthOfQuestions))}
          <RatingCsat questionId={csatQuestionId} />
        {/if}
      </div>
    </div>
  {/if}
  <!-- End Rating csat -->
{/if}

{#if $ConfigStore.customCSSUrl}
  {#if $ConfigStore.customCSSIntegrity}
    <link
      href={$ConfigStore.customCSSUrl}
      rel="stylesheet"
      type="text/css"
      integrity={$ConfigStore.customCSSIntegrity}
      crossorigin="anonymous"
    />
  {:else}
    <link href={$ConfigStore.customCSSUrl} rel="stylesheet" type="text/css" />
  {/if}
{/if}

<style lang="scss">
  @use './../../scss/components/_toggler';
</style>
